import { Button, Center, Flex, FormControl, FormControlProps, FormErrorMessage, FormLabel, HStack, Icon, Image, Text } from '@chakra-ui/react';
import HoverableIconButton from 'components/Atomos/Buttons/HoverableIconButton';
import { ChangeEvent, useRef, useState } from 'react';
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form';
import { IconType } from 'react-icons';
import { FiImage, FiX } from 'react-icons/fi';

type InputImageProps<T extends FieldValues> = FormControlProps & {
    name: Path<T>;
    label?: string;
    icon?: IconType;
    error?: FieldError;
    control: Control<T>;
}

const getSource = (value: string | File) => {
    if (!value) {
        return '';
    }

    if (typeof value === "string") {
        return value;
    }

    return URL.createObjectURL(value);
}

const InputImage = <T extends FieldValues>({
    name,
    control,
    label,
    error,
    ...rest
}: InputImageProps<T>) => {
    const [isHovering, setIsHovering] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <FormControl {...rest} isInvalid={!!error}>
            {label && (
                <FormLabel>{label}</FormLabel>
            )}
            <Controller
                name={name}
                control={control}
                render={({ field: { onChange, value } }) => {                    
                    const previewImage = getSource(value);

                    const handleImageChange = (evt: ChangeEvent<HTMLInputElement>) => {
                        const file = evt.target.files?.[0];
                        onChange(file);
                    }
                    
                    return (
                        <>
                            {value && (
                                <HStack justify="flex-end">
                                    <HoverableIconButton
                                        variant="ghost"
                                        label="Remover imagem"
                                        icon={<FiX />}
                                        onClick={() => onChange(null)}
                                    />
                                </HStack>
                            )}
                            {previewImage && (
                                <Flex position="relative">
                                    <Center
                                        onClick={() => inputRef.current?.click()}
                                        cursor="pointer"
                                        opacity={isHovering ? 1 : 0}
                                        backdropFilter="blur(3px)"
                                        onMouseOver={() => setIsHovering(true)}
                                        onMouseOut={() => setIsHovering(false)}
                                        width="100%"
                                        height="100%"
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        rounded="md"
                                        transition=".35s"
                                        bg="rgba(0, 0, 0, 0.4)"
                                    >
                                        <Text fontWeight="bold">Alterar imagem</Text>
                                    </Center>
                                    <Image
                                        src={previewImage}
                                        width="100%"
                                        transition="0.35s"
                                        rounded="md"
                                        onMouseOver={() => setIsHovering(true)}
                                        onMouseOut={() => setIsHovering(false)}
                                    />
                                </Flex>
                            )}
                            {!value && (
                                <Button
                                    isFullWidth
                                    borderWidth={error ? 2 : 0}
                                    borderColor={error ? 'red.300' : undefined}
                                    onClick={() => {
                                        inputRef.current?.click();
                                    }}
                                    maxHeight={200}
                                    variant="outline"
                                    leftIcon={<Icon as={FiImage} />}
                                >
                                    Selecionar uma imagem
                                </Button>
                            )}
                            <input
                                ref={inputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                style={{ display: "none" }}
                            />
                        </>
                    )
                }}
            />
            {error && (
                <FormErrorMessage mt="1" fontSize="xs">
                    {error.message}
                </FormErrorMessage>
            )}
        </FormControl>
    )
}

export default InputImage